import { useTranslate } from "@/locales";
import { useLocale } from "@/middleware";
import { RootState } from "@/store/type";
import { ROLE } from "@/types";
import {
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { isUndefined } from "lodash";
import { useSelector } from "react-redux";

interface Invoice {
  fileName: string;
  companyId: string;
  createdDate: string;
  userId: string;
  enVersion: string;
  arVersion: string;
}
interface Product {
  itemId: string;
  item: string;
  quantity: number;
  price: number;
  discount: number;
  notice: string;
  image: string;
  attachment: string;
  companyId: string;
  company: {
    nameAr: string;
    CompanyNr: string;
  };
}
interface Order {
  id: string;
  title: string;
  deliverDate: string;
  city: string;
  invoices: Invoice[];

  address: string;
  products: Product[];
  receivedProducts: Product[];
  Sendedproducts: Product[];
  OrderNr: number;
  status: string;
  DeliveryMethod: string;
  paymentMethod: string;
}

export function OrdersInvoices({
  order,
  isMobile,
}: {
  order: Order;
  isMobile: boolean;
}) {
  const { locale } = useLocale();
  const { t } = useTranslate(locale);
  const authUser = useSelector((state: RootState) => state.auth.authUser);
  const userRole = isUndefined(authUser?.role) ? "" : authUser?.role;
  return (
    <Card
      sx={{
        mb: 4,
        borderRadius: 3,
        boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
      }}
    >
      <CardContent sx={{ p: 3 }}>
        <Typography
          variant="h6"
          sx={{
            fontWeight: 700,
            mb: 3,
            color: "#1a1a1a",
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          📄 {t("invoices.title")}{" "}
          {userRole === ROLE.BUYER ? t("invoices.buyer") : t("invoices.seller")}
        </Typography>
        <Divider sx={{ mb: 3 }} />

        {/* Mobile Invoices View */}
        {isMobile ? (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {order.invoices.map((invoice, index) => (
              <Card
                key={index}
                sx={{
                  borderRadius: 2,
                  border: "1px solid #e0e0e0",
                }}
              >
                <CardContent sx={{ p: 2 }}>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      {t("invoices.number")}:
                    </Typography>
                    <Typography variant="body1" fontWeight={600}>
                      {invoice.fileName}
                    </Typography>
                  </Box>

                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      {t("invoices.creationDate")}:
                    </Typography>
                    <Typography variant="body1" fontWeight={600}>
                      {new Date(invoice.createdDate).toLocaleDateString(
                        "ar-SA"
                      )}
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 1,
                    }}
                  >
                    <Button
                      href={invoice.enVersion}
                      target="_blank"
                      fullWidth
                      sx={{
                        backgroundColor: "#18181b",
                      }}
                    >
                      {t("invoices.downloadEn")}
                    </Button>
                    <Button
                      href={invoice.arVersion}
                      target="_blank"
                      fullWidth
                      sx={{
                        backgroundColor: "#18181b",
                      }}
                    >
                      {t("invoices.downloadAr")}
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            ))}
          </Box>
        ) : (
          /* Desktop Invoices Table */
          <TableContainer sx={{ borderRadius: 2, overflow: "hidden" }}>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: "#f8f9fa" }}>
                  <TableCell sx={{ border: "none", fontWeight: 700 }}>
                    {t("invoices.number")}
                  </TableCell>
                  <TableCell sx={{ border: "none", fontWeight: 700 }}>
                    {t("invoices.creationDate")}
                  </TableCell>
                  <TableCell sx={{ border: "none", fontWeight: 700 }}>
                    {t("invoices.downloadEn")}
                  </TableCell>
                  <TableCell sx={{ border: "none", fontWeight: 700 }}>
                    {t("invoices.downloadAr")}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {order.invoices.map((invoice, index) => (
                  <TableRow
                    key={index}
                    sx={{
                      "&:hover": { backgroundColor: "#f8f9fa" },
                      "&:last-child td": { border: 0 },
                    }}
                  >
                    <TableCell sx={{ border: "none", fontWeight: 600 }}>
                      {invoice.fileName}
                    </TableCell>
                    <TableCell sx={{ border: "none" }}>
                      {new Date(invoice.createdDate).toLocaleDateString(
                        "ar-SA"
                      )}
                    </TableCell>
                    <TableCell sx={{ border: "none" }}>
                      <Button
                        variant="contained"
                        href={invoice.enVersion}
                        target="_blank"
                        size="small"
                        sx={{
                          backgroundColor: "#18181b",
                        }}
                      >
                        {t("invoices.downloadEn")}
                      </Button>
                    </TableCell>
                    <TableCell sx={{ border: "none" }}>
                      <Button
                        variant="contained"
                        href={invoice.arVersion}
                        target="_blank"
                        size="small"
                        sx={{
                          backgroundColor: "#18181b",
                        }}
                      >
                        {t("invoices.downloadAr")}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </CardContent>
    </Card>
  );
}
